import Bugsnag from '@bugsnag/js'
import { redirectToLogin } from '../auth'

const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  timeout: 30000,
  retryableErrors: ['Failed to fetch', 'NetworkError', 'TypeError', 'AbortError'],
  retryableStatusCodes: [408, 429, 500, 502, 503, 504]
}

function RequestHeaderBuilder() {
  const DEFAULT_REQUEST_HEADER = {
    'Content-Type': 'application/json',
    accept: 'application/json',
    'application-token': process.env.REACT_APP_TOKEN
  }

  function decorate(customRequestHeader = {}) {
    return { ...DEFAULT_REQUEST_HEADER, ...customRequestHeader }
  }
  return {
    buildHeader(headers) {
      return decorate(headers)
    }
  }
}

function RequestBuilder() {
  const { REACT_APP_FUSION_ORIGIN } = process.env

  function normalizePath(path) {
    if (path.startsWith('/')) return path.slice(1)

    return path
  }

  function buildUrl(path) {
    return `${REACT_APP_FUSION_ORIGIN}/${normalizePath(path)}`
  }

  function buildBody(payload) {
    if (!payload) {
      return {}
    }

    return {
      body: JSON.stringify(payload)
    }
  }

  function isRetryableError(error) {
    if (!error) return false
    
    if (error.name === 'AbortError') return true
    
    if (error.status && RETRY_CONFIG.retryableStatusCodes.includes(error.status)) return true
    
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) return true
    if (error.name === 'NetworkError') return true
    
    if (error.message) {
      return RETRY_CONFIG.retryableErrors.some(retryableError => 
        error.message.includes(retryableError)
      )
    }
    
    return false
  }

  function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  async function fetchWithRetry(url, options, retryCount = 0) {
    const abortController = new AbortController()
    const timeoutId = setTimeout(() => {
      abortController.abort()
    }, RETRY_CONFIG.timeout)

    try {
      const fetchOptions = {
        ...options,
        signal: abortController.signal
      }

      const response = await fetch(url, fetchOptions)
      
      clearTimeout(timeoutId)
      
      if (response.status === 401) {
        redirectToLogin()
        return Promise.reject({ status: 401, isAuthError: true })
      }
      
      if (!response.ok && RETRY_CONFIG.retryableStatusCodes.includes(response.status)) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`)
        error.status = response.status
        throw error
      }
      
      return response
    } catch (error) {
      clearTimeout(timeoutId)
      
      if (error.isAuthError) {
        throw error
      }
      
      if (error.name === 'AbortError') {
        error.message = `Request timeout after ${RETRY_CONFIG.timeout}ms`
      }
      
      if (retryCount < RETRY_CONFIG.maxRetries && isRetryableError(error)) {
        const delayMs = RETRY_CONFIG.retryDelay * Math.pow(2, retryCount)
        
        console.warn(`Request failed, retrying in ${delayMs}ms (attempt ${retryCount + 1}/${RETRY_CONFIG.maxRetries}):`, error.message)
        
        await delay(delayMs)
        return fetchWithRetry(url, options, retryCount + 1)
      }
      
      Bugsnag.notify(error, {
        metadata: {
          retryCount,
          url,
          method: options.method,
          errorType: error.name,
          isTimeout: error.name === 'AbortError'
        }
      })
      
      throw error
    }
  }

  function fetchWithAuthCheck(url, options) {
    return fetchWithRetry(url, options)
  }

  function build(path, method, payload, headers) {
    let options = {
      method,
      headers,
      mode: 'cors'
    }

    if (method !== 'GET') {
      options = { ...options, ...buildBody(payload) }
    }

    return fetchWithAuthCheck(buildUrl(path), options)
  }

  return {
    get(path, payload, headers) {
      return build(path, 'GET', payload, headers)
    },

    post(path, payload, headers) {
      return build(path, 'POST', payload, headers)
    },

    patch(path, payload, headers) {
      return build(path, 'PATCH', payload, headers)
    },

    delete(path, payload, headers) {
      return build(path, 'DELETE', payload, headers)
    }
  }
}

export default { ...RequestHeaderBuilder(), ...RequestBuilder() }
